import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row, DatePicker, Button, Space, Select } from "antd";
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import SkipAnalyticsContext from "../../../context/skip-analytics/context";
import styles from "./styles.module.scss";

const { RangePicker } = DatePicker;

interface IProps {
  showDriverFilter?: boolean;
  onFilter?: () => void;
}

const SkipAnalyticsFilter: React.FC<IProps> = ({
  showDriverFilter = false,
  onFilter,
}) => {
  const { t } = useTranslation();
  const { historyQuery, driversQuery, actions } = useContext(SkipAnalyticsContext);

  // Initialize with default dates from query (last 30 days)
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    historyQuery.from_date ? dayjs(historyQuery.from_date) : dayjs().subtract(30, "days"),
    historyQuery.to_date ? dayjs(historyQuery.to_date) : dayjs(),
  ]);
  const [skipType, setSkipType] = useState<'store' | 'driver' | undefined>(
    undefined
  );

  const skipTypeOptions = [
    { label: t("all"), value: undefined },
    { label: t("store_skip"), value: 'store' as const },
    { label: t("order_skip"), value: 'driver' as const },
  ];

  const handleFilter = () => {
    const from_date = dateRange[0]
      ? dateRange[0].format("YYYY-MM-DD")
      : dayjs().subtract(30, "days").format("YYYY-MM-DD");
    const to_date = dateRange[1]
      ? dateRange[1].format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");

    // Update both queries to keep dates synchronized
    actions.setHistoryQuery({
      ...historyQuery,
      page: 1,
      from_date,
      to_date,
      skip_type: skipType,
    });

    actions.setDriversQuery({
      ...driversQuery,
      page: 1,
      from_date,
      to_date,
    });

    if (onFilter) {
      onFilter();
    }
  };

  const handleReset = () => {
    // Reset to default: last 30 days
    const defaultFromDate = dayjs().subtract(30, "days");
    const defaultToDate = dayjs();

    setDateRange([defaultFromDate, defaultToDate]);
    setSkipType(undefined);

    const from_date = defaultFromDate.format("YYYY-MM-DD");
    const to_date = defaultToDate.format("YYYY-MM-DD");

    actions.setHistoryQuery({
      page: 1,
      per_page: historyQuery?.per_page || 10,
      from_date,
      to_date,
    });

    actions.setDriversQuery({
      ...driversQuery,
      page: 1,
      from_date,
      to_date,
    });

    if (onFilter) {
      onFilter();
    }
  };

  return (
    <div className={styles.container}>
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={24} md={10} lg={8}>
          <label className={styles.label}>{t("date_range")}</label>
          <RangePicker
            value={dateRange}
            onChange={(dates) =>
              setDateRange(dates as [Dayjs | null, Dayjs | null])
            }
            style={{ width: "100%" }}
            placeholder={[t("from_date"), t("to_date")]}
          />
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <label className={styles.label}>{t("skip_type")}</label>
          <Select
            value={skipType}
            onChange={(value) => setSkipType(value)}
            options={skipTypeOptions}
            style={{ width: "100%" }}
            placeholder={t("select_skip_type")}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div className={styles.buttons}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleFilter}
              >
                {t("filter")}
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                {t("reset")}
              </Button>
            </Space>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default SkipAnalyticsFilter;
